/* RESPONSIBLE TEAM: team-inbox-experience */
import Controller from '@ember/controller';
import ENV from 'embercom/config/environment';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { timeout } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import { DEFAULT_MACRO_TYPES } from 'embercom/lib/inbox/constants';
import ajax from 'embercom/lib/ajax';
import { MacroVisibility } from 'embercom/objects/inbox/macro';
import Range from 'embercom/models/reporting/range';

const EXCLUDED_PRESETS = ['past_12_months', 'year_to_date'];

class ExportRange extends Range {
  static create(timezone) {
    let base = Range.createFromParams(null, null, timezone);
    let range = new ExportRange(base.start, base.end, timezone);
    range.selectedRange = null;
    return range;
  }

  get presetToTranslationKey() {
    let presets = super.presetToTranslationKey;
    EXCLUDED_PRESETS.forEach((key) => delete presets[key]);
    return presets;
  }
}

const CREATE_EDIT_MACROS_FF = 'spg-inbox-create-edit-macros';

export default class MacrosController extends Controller {
  @service appService;
  @service store;
  @service intercomConfirmService;
  @service notificationsService;
  @service savedReplySearchService;
  @service intercomEventService;
  @service csv;
  @service permissionsService;
  @service permissionsModal;
  @service intl;
  @service guideLibraryService;
  @service router;

  @tracked selectedReply;
  @tracked editingReply = false;
  @tracked editorDirty = false;
  @tracked replies = this.model.savedReplies;
  @tracked meta = this.model.meta;
  @tracked sortedReplies = [];
  @tracked mode = null;
  @tracked isLoadingResults = false;

  @tracked query = '';
  @tracked teamId = null;
  @tracked replyType = null;
  @tracked createdByMe = false;
  @tracked exportRange = null;
  queryParams = ['mode'];

  get teamFilterParam() {
    return this.router.currentRoute?.queryParams?.teamFilter;
  }

  get app() {
    return this.appService.app;
  }

  get initiallySelectedReply() {
    if (this.appService.app.canUseSavedRepliesFuzzySearch) {
      return this.initialReplyFromFuzzySearch;
    } else {
      return this.initialReply;
    }
  }

  get initialReplyFromFuzzySearch() {
    if (this.canManageMacros) {
      return this.sortedReplies[0];
    } else {
      return this.sortedReplies.filter((reply) => {
        return reply.visibility === MacroVisibility.Myself;
      }).firstObject;
    }
  }

  get initialReply() {
    if (this.canManageMacros) {
      return this.sortedReplies.filter((reply) => {
        return (
          !(reply.isNew || reply.isDeleted) &&
          (reply.visibility !== MacroVisibility.Myself || this.canUsePersonalMacros)
        );
      }).firstObject;
    } else if (this.canUsePersonalMacros) {
      return this.sortedReplies.filter((reply) => {
        return reply.visibility === MacroVisibility.Myself && !(reply.isNew || reply.isDeleted);
      }).firstObject;
    }
  }

  get hasUserCreatedMacros() {
    let filteredMacros = this.replies?.find(
      (reply) => !reply.name?.toLocaleLowerCase().includes('example'),
    );
    return Boolean(filteredMacros?.name);
  }

  get savedRepliesPresent() {
    return this.replies.length;
  }

  get isSearchingOrLoading() {
    return this.searchParamsPresent || this.isLoadingResults;
  }

  get searchParamsPresent() {
    return (
      this.query.length > 0 || this.replyType !== null || this.teamId !== null || this.createdByMe
    );
  }

  get searchResultsPresent() {
    return this.sortedReplies.length;
  }

  get hasUnsavedChanges() {
    return this.editorDirty;
  }

  get readOnly() {
    return false;
  }

  get confirmationModalContent() {
    return {
      title: this.intl.t('inbox.manage-macros.saved-replies.delete-confirmation-title'),
      body: this.intl.t('inbox.manage-macros.saved-replies.delete-confirmation-body'),
      confirmButtonText: this.intl.t(
        'inbox.manage-macros.saved-replies.delete-confirmation-confirm-button',
      ),
      cancelButtonText: this.intl.t(
        'inbox.manage-macros.saved-replies.delete-confirmation-cancel-button',
      ),
    };
  }

  get createDisabled() {
    return this.readOnly || this.selectedReply?.isNew || this.createSavedReply.isRunning;
  }

  get canManageMacros() {
    let permissions = this.app.currentAdmin?.currentAppPermissions;
    if (!permissions) {
      return true;
    }
    return permissions.can_manage_saved_replies;
  }

  get canUsePersonalMacros() {
    let { can_use_personal_macros } = this.app.currentAdmin?.currentAppPermissions || {};
    return can_use_personal_macros;
  }

  get hasCreateEditMacrosFF() {
    return this.app.canUseFeature(CREATE_EDIT_MACROS_FF);
  }

  get canCreateMacros() {
    let permissions = this.app.currentAdmin?.currentAppPermissions;
    return permissions?.can_create_macros ?? true;
  }

  get canCreateSharedMacros() {
    if (!this.hasCreateEditMacrosFF) {
      return this.canManageMacros;
    }
    return this.canManageMacros && this.canCreateMacros;
  }

  get canCreateAnyMacro() {
    if (this.hasCreateEditMacrosFF) {
      let canCreateSharedMacros = this.canManageMacros && this.canCreateMacros;
      return canCreateSharedMacros || this.canUsePersonalMacros;
    }
    return this.canManageMacros || this.canUsePersonalMacros;
  }

  get canDeleteSharedMacros() {
    if (!this.hasCreateEditMacrosFF) {
      return true;
    }
    let permissions = this.app.currentAdmin?.currentAppPermissions;
    return permissions?.can_manage_saved_replies && permissions?.can_delete_macros;
  }

  _canDeleteSpecificMacro(macro) {
    if (!this.hasCreateEditMacrosFF) {
      return true;
    }

    let isPersonal = macro.visibility === MacroVisibility.Myself;

    if (isPersonal) {
      let isOwner = String(macro.admin?.id) === String(this.app.currentAdmin?.id);
      return this.canUsePersonalMacros && isOwner;
    }

    return this.canDeleteSharedMacros;
  }

  get canDeleteSelectedReply() {
    if (!this.selectedReply) {
      return true;
    }
    return this._canDeleteSpecificMacro(this.selectedReply);
  }

  get canEditSharedMacros() {
    if (!this.hasCreateEditMacrosFF) {
      return true;
    }
    let permissions = this.app.currentAdmin?.currentAppPermissions;
    return permissions?.can_manage_saved_replies && permissions?.can_edit_macros;
  }

  _canEditSpecificMacro(macro) {
    if (!this.hasCreateEditMacrosFF) {
      return true;
    }

    if (!macro) {
      return true;
    }

    // For new macros, use legacy behavior
    if (macro.isNew) {
      return true;
    }

    let isPersonal = macro.visibility === MacroVisibility.Myself;

    if (isPersonal) {
      let isOwner = String(macro.admin?.id) === String(this.app.currentAdmin?.id);
      return this.canUsePersonalMacros && isOwner;
    }

    return this.canEditSharedMacros;
  }

  get canEditSelectedReply() {
    if (!this.selectedReply) {
      return true;
    }
    return this._canEditSpecificMacro(this.selectedReply);
  }

  get adminCanExportCsv() {
    return this.permissionsService.currentAdminCan('can_export_csv');
  }

  get showExportButton() {
    return this.adminCanExportCsv;
  }

  get showExportDateRange() {
    return this.app.canUseFeature('hsg-inbox-macro-usage-export-extn');
  }

  get hasMacrosToShow() {
    let isEditing = this.searchResultsPresent || this.editingReply;
    let filteredMacros = this.replies?.filter(
      (macro) => this.canUsePersonalMacros || macro.visibility !== MacroVisibility.Myself,
    ).length;

    return (
      (isEditing && (filteredMacros || this.selectedReply)) ||
      this.searchParamsPresent ||
      this.isLoadingResults
    );
  }

  @task({ drop: true })
  *updateSelectedReply(savedReply) {
    if (!(yield this.abortEditing.perform())) {
      return;
    }
    if (savedReply) {
      this.editingReply = true;
    }
    this.selectedReply = savedReply;
  }

  @action
  updateEditState(newState) {
    this.editingReply = newState;
  }

  @action
  updateDirtyState(newState) {
    this.editorDirty = newState;
  }

  @action
  cancelEditingReply() {
    if (this.selectedReply) {
      if (this.selectedReply.isNew) {
        this.selectedReply.deleteRecord();
        this.sortedReplies.removeObject(this.selectedReply);
        this.selectedReply = this.initiallySelectedReply;
      } else {
        this.selectedReply.rollbackAttributes();
      }
    }

    this.editingReply = false;
    this.editorDirty = false;
  }

  @action
  openMacrosArticleInMessenger() {
    window.Intercom('showArticle', 6433193);
    this.triggerAnalytics('creating_and_managing_macros', 'discovery_banner_link');
  }

  @action
  saveSelectedReply(selectedReply, newName, composer, selectedTeams, newVisibility, newTypes) {
    let blocks = composer.getBlocks();
    selectedReply.blocks = blocks;
    selectedReply.name = newName;
    selectedReply.visibleToTeamIds = selectedTeams;
    selectedReply.visibility = newVisibility;

    if (newTypes) {
      selectedReply.types = newTypes;
    }

    let actionTypes = selectedReply.actions?.map((a) => a.type) || [];
    let event = {
      action: 'saved',
      object: 'macros.save',
      place: 'macros',
      assign: actionTypes.any(
        (a) => a === 'assign-conversation' || a === 'assign-conversation-to-owner',
      ),
      close: actionTypes.any((a) => a === 'close-conversation'),
      snooze: actionTypes.any((a) => a === 'snooze-conversation'),
      tag: actionTypes.any((a) => a === 'add-tag-to-conversation'),
      priority: actionTypes.any((a) => a === 'change-conversation-priority'),
      cda: actionTypes.any((a) => a === 'set-conversation-data-attribute'),
      has_text: blocks.length > 0,
      visibility: selectedReply.visibility,
    };
    if (selectedTeams) {
      event.visibilityRestrictedToTeamsCount = selectedTeams.length;
    }
    this.intercomEventService.trackAnalyticsEvent(event);
    return this.saveReply.perform(selectedReply);
  }

  @action
  deleteSelectedReply(selectedReply) {
    return this.deleteReply.perform(selectedReply);
  }

  @action
  async export() {
    let range = this.exportRange;
    if (range) {
      let rangeInDays = range.endMoment.diff(range.startMoment, 'days') + 1;
      if (rangeInDays > 186) {
        this.notificationsService.notifyError(
          this.intl.t('settings.saved-replies.header.export-button.max-range-error'),
        );
        return;
      }
    }
    this.fetchUsageDataByEmail.perform();
  }

  @action
  initExportRange() {
    if (!this.exportRange) {
      let timezone = this.appService.app.timezone || 'UTC';
      this.exportRange = ExportRange.create(timezone);
    }
  }

  @action
  async updateExportRange(range) {
    this.exportRange = range;
    await this.export();
    // Unmount the DateRangeFilter to close any open dropdowns
    this.exportRange = null;
    // Wait for render cycle to complete the unmount, then remount fresh
    await new Promise((resolve) => setTimeout(resolve, 0));
    this.initExportRange();
  }

  @task({ drop: true })
  *fetchUsageDataByEmail() {
    this.intercomEventService.trackEvent('export-csv', {
      action: 'exported',
      object: 'csv',
      place: 'settings',
      type: 'saved_reply',
    });

    try {
      yield this.fetchUsageData.perform();
      this.notificationsService.notifyConfirmation(
        this.intl.t('inbox.manage-macros.saved-replies.export-email-success'),
      );
    } catch (_e) {
      this.notificationsService.notifyError(
        this.intl.t('inbox.manage-macros.saved-replies.export-error'),
      );
    }
  }

  @task({ drop: true })
  *fetchUsageData() {
    try {
      let data = {
        app_id: this.appService.app.id,
        admin_id: this.appService.app.currentAdmin.id,
      };
      let range = this.exportRange;
      if (range?.startMoment && range?.endMoment) {
        data.start_date = range.startMoment.format('YYYY-MM-DD');
        data.end_date = range.endMoment.format('YYYY-MM-DD');
      }
      return yield ajax({
        url: '/ember/saved_reply/export_usage',
        type: 'GET',
        data,
      });
    } catch (_e) {
      this.notificationsService.notifyError(
        this.intl.t('inbox.manage-macros.saved-replies.export-error'),
      );
    }
  }

  @task({ drop: true })
  *createSavedReply() {
    if (!this.canCreateAnyMacro) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'permission_denied',
        object: 'macros',
        permission: 'manage_shared_macros',
        place: 'macros_settings',
      });
      this.permissionsModal.showModal({
        permissionsLabel: this.intl.t(
          'inbox.manage-macros.saved-replies.create-macro-permission-label',
        ),
      });
      return;
    }

    if (!(yield this.abortEditing.perform())) {
      return;
    }

    let appId = this.model.app.id;
    // Default to personal macro if user can't manage macros
    let visibility = this.canManageMacros ? MacroVisibility.Everyone : MacroVisibility.Myself;
    let newReply = this.store.createRecord('saved-reply', {
      app_id: appId,
      visibleToTeamIds: null,
      visibility,
      types: DEFAULT_MACRO_TYPES.mapBy('id'),
    });

    this.selectedReply = newReply;
    this.updateEditState(true);

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'macros_new_macro',
      place: 'macros',
    });
  }

  @task({ drop: true })
  *saveReply(reply) {
    if (reply.validationMessage !== undefined) {
      this.notificationsService.notifyError(reply.validationMessage);
      return;
    }

    if (!reply.availabilityValid) {
      this.notificationsService.notifyError(
        this.intl.t('inbox.manage-macros.saved-replies.availability-error'),
      );
      return;
    }
    let isNew = reply.isNew;

    try {
      yield reply.save();
    } catch (error) {
      console.error(error);

      if (error.jqXHR && error.jqXHR.status === 422) {
        if (
          error.jqXHR.responseJSON.errors.name?.length &&
          error.jqXHR.responseJSON.errors.name[0] === 'has already been taken'
        ) {
          this.notificationsService.notifyError(
            this.intl.t('inbox.manage-macros.saved-replies.name-taken-error'),
          );
          return;
        }
        if (error.jqXHR.responseJSON.errors.blocks?.length) {
          this.notificationsService.notifyError(error.jqXHR.responseJSON.errors.blocks[0]);
          return;
        }
      }

      let operationType = reply.isNew
        ? this.intl.t('inbox.manage-macros.saved-replies.create')
        : this.intl.t('inbox.manage-macros.saved-replies.update');
      this.notificationsService.notifyError(
        this.intl.t('inbox.manage-macros.saved-replies.save-error', { type: operationType }),
      );
      return;
    }

    if (reply.actions.length > 0) {
      this.intercomEventService.trackEvent('created_macros');
    }

    if (isNew) {
      // If it's a first macro we need to fetch the list
      if (this.replies.length === 0) {
        yield this.fetchSavedReplies.perform();
      } else {
        // If we have a list we can manually add the new macro
        // so user can see it right away
        this.sortedReplies.unshiftObject(reply);
        this.meta = {
          ...this.meta,
          total_count: this.meta.total_count + 1, // and additionally update the total cound so it matches
        };
      }
      this.selectedReply = reply;
    }

    this.notificationsService.notifyConfirmation(
      this.intl.t('inbox.manage-macros.saved-replies.save-success'),
    );
    if (this.guideLibraryService.canUseGuideLibraryService) {
      yield this.guideLibraryService.markStepCompleted('display_only_create_your_macros');
    }
    this.editorDirty = false;
  }

  @task({ drop: true })
  *deleteReply(reply, composer) {
    try {
      yield reply.destroyRecord();
    } catch (error) {
      console.error(error);
      this.notificationsService.notifyError(
        this.intl.t('inbox.manage-macros.saved-replies.delete-error'),
      );
      reply.rollbackAttributes();
      return;
    }

    yield this.refreshSavedRepliesList.perform();

    this.notificationsService.notifyConfirmation(
      this.intl.t('inbox.manage-macros.saved-replies.delete-success'),
    );
    this.selectedReply = this.initiallySelectedReply;

    if (composer) {
      composer.send('load', this.selectedReply?.blocks);
    }

    this.editingReply = false;
  }

  @task({ drop: true })
  *duplicateReply(reply) {
    if (this.hasCreateEditMacrosFF && !this.canCreateAnyMacro) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'permission_denied',
        object: 'macros_duplicate',
        permission: 'manage_shared_macros',
        place: 'macros_settings',
      });
      this.permissionsModal.showModal({
        permissionsLabel: this.intl.t(
          'inbox.manage-macros.saved-replies.create-macro-permission-label',
        ),
      });
      return;
    }

    let newReply;
    try {
      newReply = yield ajax({
        url: `/ember/saved_replies/${reply.id}/duplicate`,
        type: 'POST',
        contentType: 'application/json',
        dataType: 'JSON',
        data: JSON.stringify({
          app_id: this.app.id,
        }),
      });
      this.store.pushPayload('saved-reply', {
        'saved-reply': newReply,
      });
    } catch (error) {
      console.error(error);
      this.notificationsService.notifyError(
        this.intl.t('inbox.manage-macros.saved-replies.duplicate-error'),
      );
      return;
    }
    this.sortedReplies.insertAt(
      this.sortedReplies.indexOf(reply) + 1,
      this.store.peekRecord('saved-reply', newReply.id),
    );
    if (this.meta?.total_count != null) {
      this.meta = {
        ...this.meta,
        total_count: this.meta.total_count + 1,
      };
    }

    this.notificationsService.notifyConfirmation(
      this.intl.t('inbox.manage-macros.saved-replies.duplicate-success'),
    );
    this.selectedReply = this.store.peekRecord('saved-reply', newReply.id);
  }

  @task({ drop: true })
  *initSavedReplySearch() {
    if (this.app.canUseSavedRepliesFuzzySearch) {
      let results = yield this.savedReplySearchService.search.perform();
      this.sortedReplies = results;
    } else {
      this.sortedReplies = this.replies.sortBy('name');
    }

    if (this.mode === 'create') {
      yield this.createSavedReply.perform();
    } else {
      this.selectedReply = this.initiallySelectedReply;
    }
  }

  @task({ drop: true })
  *fetchSavedReplies() {
    this.isLoadingResults = true;
    try {
      yield this.store.query('saved-reply', this.savedRepliesQueryParams(1)).then((results) => {
        this.replies = results;
        this.sortedReplies = results.toArray();
        this.meta = results.meta;
        this.selectedReply = this.initiallySelectedReply;
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('inbox.manage-macros.saved-replies.fetch-error'),
      );
    } finally {
      this.isLoadingResults = false;
    }
  }

  savedRepliesQueryParams(page) {
    let queryParams = {
      page,
      per_page: 50,
      type: this.replyType,
      search: this.query,
      created_by_admin: this.createdByMe,
    };

    if (this.teamId === 'everyone') {
      queryParams.visibility = 'everyone';
    } else if (this.teamId) {
      queryParams.team_id = this.teamId;
    }

    return queryParams;
  }

  @task({ restartable: true })
  *debouncedSearch(query = '') {
    yield timeout(ENV.APP._200MS);
    this.query = query;

    if (!(yield this.abortEditing.perform())) {
      return;
    }

    yield this.fetchSavedReplies.perform();
  }

  @task({ restartable: true })
  *refreshSavedRepliesList() {
    yield this.debouncedSearch.perform(this.query);
  }

  @task({ drop: true })
  *abortEditing() {
    if (this.saveReply.isRunning || !this.hasUnsavedChanges) {
      return true;
    }

    let canAbandonWithoutChecking = !this.editorDirty;
    let userWantsToContinue;

    if (!canAbandonWithoutChecking) {
      userWantsToContinue = yield this.intercomConfirmService.confirm(
        this.confirmationModalContent,
      );
    }
    let shouldAbandonChanges = canAbandonWithoutChecking || !userWantsToContinue;

    if (shouldAbandonChanges) {
      this.cancelEditingReply();
      return true;
    } else {
      return false;
    }
  }

  @task({ drop: true })
  *loadMoreSavedReplies() {
    if (!this.meta || !this.meta.next_page) {
      return true;
    }

    let savedReplies = yield this.store.query(
      'saved-reply',
      this.savedRepliesQueryParams(this.meta.next_page),
    );
    this.sortedReplies = [...this.sortedReplies, ...savedReplies.toArray()];
    this.meta = savedReplies.meta;
    return true;
  }

  @action
  triggerAnalytics(object, section = 'dropdown_item') {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object,
      context: `onboarding_guide_library.settings.macros.banner.${section}`,
      place: 'settings.saved-replies-macros',
      section,
    });
  }

  @action
  async updateVisibilityFilters(replyType = null, teamId = null, createdByMe = false) {
    this.replyType = replyType;
    this.teamId = teamId;
    this.createdByMe = createdByMe;
    await this.fetchSavedReplies.perform();
  }
}
